import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportMatchingPdf } from '../../services/matchingApi.js';

/**
 * PDF エクスポートボタン (SCR-M-001、BTN-M-003、T-003-U1、Issue #470)
 *
 * 押下時に GET /api/matching/{opportunity_id}/pdf を呼出し、PDF バイナリを取得後
 * 既存の汎用ファイルビューア (`/file-viewer`) へ Blob URL を渡してプレビュー表示する
 * (BE はテナント分離のため認証必須のAPIエンドポイントとして実装されており、
 * FileViewerPage が前提とする署名付きURLとは異なるため、本コンポーネント側で
 * 認証済み `api.fetch` 経由の取得を行い、生成した Blob URL を渡す)。
 *
 * AI ポイントの残量では押せなくしない。PDF 出力は AI を呼ばず、ポイントを消費しないため
 * (BTN-M-003 ② と §5.2 No.6 の廃止、Issue #1208。改める前は Issue #882 で残量を判定していた)。
 *
 * @param {{opportunityId: string, matchingResultId: string, disabled?: boolean, onExportFailure?: () => void,
 *   onExportingChange?: (exporting: boolean) => void}} props
 *   matchingResultId: 画面に表示中のマッチング結果の ID。PDF はこの結果から作られる (SCR-M-001 §4.3 BTN-M-003、Issue #878)。
 *   並べ替えの保存と同じ値を渡す。
 *   onExportFailure: 出力が失敗したときに呼ぶ (残量の取り直し。§6.4.4「取り直す時機」④。
 *   Issue #1208 以降は要らないが、害が無いため残す)
 *   onExportingChange: 出力中になった・終わったときに呼ぶ (出力中は求人プールからの削除〔BTN-M-014〕を押せなくする
 *   ため。SCR-M-001 §4.3、Issue #883)
 */
function PdfExportButton({ opportunityId, matchingResultId, disabled, onExportFailure, onExportingChange }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleClick = async () => {
        setLoading(true);
        onExportingChange?.(true);
        setErrorMessage(null);

        let result;
        try {
            result = await exportMatchingPdf(opportunityId, matchingResultId);
        } finally {
            setLoading(false);
            onExportingChange?.(false);
        }

        if (!result.ok) {
            setErrorMessage(result.errorMessage);
            // 残量を取り直す (§6.4.4 ④)。PDF 出力はポイントを消費しなくなったが (Issue #1208)、
            // BE が先に古い版のままの間は消費しうるため残す
            onExportFailure?.();
            return;
        }

        const blobUrl = URL.createObjectURL(result.blob);
        navigate('/file-viewer', {
            state: {
                downloadUrl: blobUrl,
                filename: result.fileName,
                mimeType: 'application/pdf',
                title: '推薦PDF プレビュー',
                subtitle: 'AI マッチング推薦結果（職業安定法 Agent-in-the-loop 対応）',
            },
        });
    };

    return (
        <div className="matching-pdf-export">
            <button
                type="button"
                className="modal-button-cancel"
                onClick={handleClick}
                disabled={disabled || loading}
            >
                {loading ? 'PDF生成中...' : 'PDF エクスポート'}
            </button>
            {errorMessage && (
                <span className="matching-reorder-error" role="alert">{errorMessage}</span>
            )}
        </div>
    );
}

export default PdfExportButton;
